
import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from '@supabase/supabase-js';
import { toast } from "sonner";
import { AuthContextType } from "@/types/authTypes";
import { setupNativeAuthListener } from "@/utils/nativeAuth";

// Utility function to clean up auth state
const cleanupAuthState = () => {
  // Remove standard auth tokens
  localStorage.removeItem('supabase.auth.token');
  
  // Remove all Supabase auth keys from localStorage that might cause conflicts
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      localStorage.removeItem(key);
    }
  });
  
  // Remove from sessionStorage if in use
  Object.keys(sessionStorage || {}).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      sessionStorage.removeItem(key);
    }
  });
};

// Create context with a default value
const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  isPremium: false,
  isParentMode: false, // Add default value for isParentMode
  signUp: async () => ({ error: null, data: null }),
  signIn: async () => ({ error: null, data: null }),
  signOut: async () => {},
  refreshPremiumStatus: async () => false,
});

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// AuthProvider component to wrap the app
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [isParentMode, setIsParentMode] = useState(false); // Add state for parent mode

  // Initialize authentication and set up listener
  useEffect(() => {
    let isMounted = true; // Add mounted flag to prevent state updates after unmount
    
    // IMPORTANT: Set up auth state listener FIRST to prevent missing auth events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        if (!isMounted) return;
        
        console.log("Auth state changed:", event);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        // Check user premium status when auth changes
        if (currentSession?.user?.id) {
          // Use setTimeout to prevent Supabase auth deadlock
          setTimeout(() => {
            checkPremiumStatus(currentSession.user.id);
          }, 0);
        } else {
          setIsPremium(false);
        }
      }
    );

    // THEN check for existing session
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        
        if (initialSession?.user?.id) {
          checkPremiumStatus(initialSession.user.id);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        toast.error("Failed to initialize authentication");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    // Completes native (iOS/Android) OAuth sign-in when the in-app browser
    // redirects back to the app via the custom URL scheme.
    const removeNativeAuthListener = setupNativeAuthListener();

    // Cleanup function
    return () => {
      isMounted = false;
      subscription.unsubscribe();
      removeNativeAuthListener();
    };
  }, []);

  // Function to check premium status
  const checkPremiumStatus = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("is_premium")
        .eq("id", userId)
        .single();

      if (error) {
        console.error("Failed to check premium status:", error);
        return;
      }

      setIsPremium(!!data?.is_premium);
    } catch (error) {
      console.error("Error checking premium status:", error);
    }
  };

  // Function to refresh premium status
  const refreshPremiumStatus = async () => {
    if (user?.id) {
      await checkPremiumStatus(user.id);
      return isPremium;
    }
    return false;
  };

  // Sign up function with clean up
  const signUp = async (email: string, password: string, userData: Record<string, any> = {}) => {
    try {
      // Clean up existing auth state first
      cleanupAuthState();
      
      // Try to sign out globally first
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
        console.log("Global sign out failed, continuing with signup");
      }
      
      const response = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/app`,
        },
      });

      if (response.error) {
        toast.error(response.error.message);
        return { error: response.error, data: null };
      }

      toast.success("Check your email to confirm your account!");
      return { error: null, data: response.data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Failed to sign up");
      toast.error(err.message);
      return { error: err, data: null };
    }
  };

  // Sign in function with clean up
  const signIn = async (email: string, password: string) => {
    try {
      // Clean up existing auth state first
      cleanupAuthState();
      
      // Try to sign out globally first
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
        console.log("Global sign out failed, continuing with signin");
      }
      
      const response = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (response.error) {
        toast.error(response.error.message);
        return { error: response.error, data: null };
      }

      return { error: null, data: response.data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Failed to sign in");
      toast.error(err.message);
      return { error: err, data: null };
    }
  };

  // Sign out function with clean up
  const signOut = async () => {
    try {
      // Clean up auth state first
      cleanupAuthState();
      
      // Attempt global sign out
      await supabase.auth.signOut({ scope: 'global' });
      
      // Clear user state immediately 
      setUser(null);
      setSession(null);
      
      toast.success("Signed out successfully");
      
      // Force a page reload for a clean slate
      window.location.href = '/auth';
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error("Failed to sign out");
      
      // Attempt to force cleanup anyway
      setUser(null);
      setSession(null);
      window.location.href = '/auth';
    }
  };

  // Create value object for the context
  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isPremium,
    isParentMode, // Add isParentMode to the context value
    signUp,
    signIn,
    signOut,
    refreshPremiumStatus,
  };

  // Return the provider with the value
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
